import * as t from 'io-ts';
import { Logger } from '../../logger';
import { Cache } from '../cache/types';

export interface IMetroClient {
  getAvailableAppointmentSlots: (
    cache: Cache,
    signal: AbortSignal,
    logger: Logger,
    trackingNumber: string,
    retailerMoniker: string,
  ) => Promise<AvailableTimeSlotsResponse | StatusMessageResponse>;
  scheduleAppointment: (
    cache: Cache,
    signal: AbortSignal,
    logger: Logger,
    date: Date,
    trackingNumber: string,
    retailerMoniker: string,
  ) => Promise<StatusMessageResponse>;
  rescheduleAppointment: (
    cache: Cache,
    signal: AbortSignal,
    logger: Logger,
    date: Date,
    trackingNumber: string,
    retailerMoniker: string,
  ) => Promise<StatusMessageResponse>;
  updateContactInfo: (
    cache: Cache,
    signal: AbortSignal,
    logger: Logger,
    postData: IUpdateContactInfoPostData,
    trackingNumber: string,
    retailerMoniker: string,
  ) => Promise<StatusMessageResponse>;
  getScheduledAppointment: (
    cache: Cache,
    signal: AbortSignal,
    logger: Logger,
    trackingNumber: string,
    retailerMoniker: string,
  ) => Promise<GetScheduledAppointmentResponse | StatusMessageResponse>;
}

interface IWeekofMonth {
  weekNo: number;
  weekStartDate: string;
}

export interface IAvailableTimeSlotsPostData {
  trackingNo: string;
  zipCode?: string;
  scheduleType: ScheduleType;
}

export interface IScheduleDeliveryPostData {
  trackingNo: string;
  date: string;
  weekofMonth: IWeekofMonth | null;
  scheduleType: ScheduleType;
  webUserID: string;
}

export interface IGetScheduledAppointmentPostData {
  scheduleType: ScheduleType;
  trackingNo: string;
}

export interface IUpdateContactInfoPostData {
  trackingNo: string;
  email: string;
  phone: string;
  extension: string;
  mobile: string;
  updateFor: ScheduleType;
}

const AppointmentStatusV = t.keyof({
  Success: null,
  Failed: null,
  ALM: null,
  ANP: null,
  ANA: null,
  CAN: null,
  CAP: null,
  EM: null,
  DAS: null,
  DCO: null,
  DLC: null,
  DH: null,
  PAS: null,
  PCO: null,
  PH: null,
  PLC: null,
  POH: null,
  RNA: null,
  RQD: null,
  SWXD: null,
  TNW: null,
  TR: null,
});

// 0 - No, 1 - Yes
const AppointmentAvailableV = t.union([t.literal(0), t.literal(1)]);

// 1 - Pickup, 2 - Delivery
type ScheduleType = '1' | '2';

const ScheduleTypeV = t.keyof({
  '1': null,
  '2': null,
});

export const AuthorizationTokenResponseV = t.strict({
  access_token: t.string,
  token_type: t.literal('bearer'),
  expires_in: t.number,
  'as:client_id': t.string,
  userName: t.string,
  id: t.string,
  refresh_token: t.string,
  '.issued': t.string,
  '.expires': t.string,
});

export type AuthorizationTokenResponse = t.TypeOf<
  typeof AuthorizationTokenResponseV
>;

const IWeekofMonthV = t.strict({
  weekNo: t.number,
  weekStartDate: t.string,
});

const IAvailableWeekofMonthV = t.strict({
  weekNumber: t.string,
  daysofMonth: t.string,
});

export const StatusMessageResponseV = t.strict({
  status: AppointmentStatusV,
  message: t.string,
});

export type StatusMessageResponse = t.TypeOf<typeof StatusMessageResponseV>;

export const AvailableTimeSlotsResponseV = t.strict({
  appointmentAvailable: AppointmentAvailableV,
  exceptionDates: t.union([t.string, t.null]),
  datesofMonth: t.string,
  weeksofMonth: t.array(IAvailableWeekofMonthV),
  specificDay: t.union([t.string, t.null]),
  scheduleType: ScheduleTypeV,
});

export type AvailableTimeSlotsResponse = t.TypeOf<
  typeof AvailableTimeSlotsResponseV
>;

export const GetScheduledAppointmentResponseV = t.strict({
  scheduledDate: t.union([t.string, t.null]),
  scheduledWeek: t.union([IWeekofMonthV, t.null]),
});

export type GetScheduledAppointmentResponse = t.TypeOf<
  typeof GetScheduledAppointmentResponseV
>;
